import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { X, TrendingUp, TrendingDown, Minus, ArrowUpRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";

/**
 * EliteNavMenu — Vinci "Glass Cockpit" full-screen cinematic navigation.
 *
 * Trigger: <MonumentalMenuTrigger /> (3 data-lines + gold Cinzel "MENU")
 * Overlay: full-screen glassmorphism, obsidian-grid video visible through
 *           the frosted layers.
 * Left · Vinci Shield + "Veni · Vidi · Vici"
 * Centre · primary links in Cinzel with monogram pill + technical
 *          description that fades in on hover.
 * Right · Live Market Pulse (Tata Tiscon, UltraTech, Ambuja, etc.)
 * "Wow" · cyan bioluminescent cursor halo tracks the mouse while the
 *          menu is open.
 */

// The link roster — mirrors current site IA + surfaces the Vinci
// "big rocks" you'd want prominent inside a cinematic menu.
const SECTION_LINKS = [
  { label: "About", href: "#about" }, { label: "Services", href: "#services" }, { label: "Materials", href: "#materials" },
  { label: "Group", href: "#group" }, { label: "Projects", href: "#projects" }, { label: "Contact", href: "#contact" },
];

const CORE_LINKS = [
  { to: "/",          mono: "HQ", label: "Vinci Headquarters",
    caption: "Return to the crafted-for-constructions homepage" },
  { to: "/shop",      mono: "TT", label: "Trade Terminal",
    caption: "Enter the 5151 Trading Core · shop live inventory" },
  { to: "/trends",    mono: "PP", label: "Price Pulse",
    caption: "Real-time Tata Tiscon, UltraTech &amp; cement rates" },
  { to: "/league",    mono: "BC", label: "Big-Cap League",
    caption: "Elite counterparty circle · invite only" },
  { to: "/property-desk", mono: "PD", label: "Property Desk", gold: true,
    caption: "Exceptional Properties · Verified Network" },
  { to: "/network",   mono: "NW", label: "Partner Network",
    caption: "Custodians, sellers &amp; guarantors nationwide" },
  { to: "/leadership",mono: "LX", label: "Leadership Suite",
    caption: "Meet the Vinci Trade Desk" },
  { to: "/careers",   mono: "CR", label: "Careers",
    caption: "Join the 2% · Vinci Recruit desk" },
  { to: "/request-quote", mono: "RQ", label: "Request a Quote",
    caption: "Pre-flight validated RFQ · 8-field precision" },
];

export function EliteNavMenu({ open, onClose }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const overlayRef = useRef(null);
  const [cursor, setCursor] = useState({ x: 0, y: 0, visible: false });
  const [rates, setRates] = useState([]);
  const [hovered, setHovered] = useState(null);
  const accountPath = user && user.role === "admin" ? "/admin" : "/account";

  // Lock scroll while open + preload live rates for the right rail.
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const controller = new AbortController();
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rates`,
             { signal: controller.signal })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        if (!d) return;
        const arr = Array.isArray(d) ? d : (d.rates || []);
        setRates(arr.filter((x) => x?.price).slice(0, 14));
      })
      .catch(() => { /* offline · silent */ });
    return () => {
      document.body.style.overflow = original;
      controller.abort();
    };
  }, [open]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const track = (e) => {
    const el = overlayRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setCursor({ x: e.clientX - r.left, y: e.clientY - r.top, visible: true });
  };

  const go = (to) => { onClose?.(); setTimeout(() => nav(to), 220); };
  const goSection = (href) => {
    onClose?.();
    const scroll = () => document.querySelector(href)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => { if (window.location.pathname !== "/") { nav("/"); setTimeout(scroll, 400); } else scroll(); }, 220);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="elite-nav"
          ref={overlayRef}
          data-testid="elite-nav-overlay"
          role="dialog" aria-modal="true" aria-label="Vinci · Elite Navigation"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          onMouseMove={track}
          onMouseLeave={() => setCursor((c) => ({ ...c, visible: false }))}
          className="fixed inset-0 z-[100] overflow-hidden"
          style={{
            background:
              "radial-gradient(1400px 700px at 20% 0%, rgba(216,178,62,0.10), transparent 60%),"
              + "radial-gradient(1200px 700px at 90% 100%, rgba(47,185,153,0.09), transparent 60%),"
              + "linear-gradient(180deg,rgba(5,5,6,0.72) 0%, rgba(5,5,6,0.86) 100%)",
            backdropFilter: "blur(28px) saturate(140%)",
            WebkitBackdropFilter: "blur(28px) saturate(140%)",
          }}
        >
          {/* Obsidian video (menu inherits the same asset the terminal uses) */}
          <video
            className="absolute inset-0 w-full h-full object-cover opacity-[0.32] pointer-events-none"
            src="/assets/obsidian-grid.webm"
            poster="/assets/obsidian-grid-poster.png"
            autoPlay muted loop playsInline preload="auto" aria-hidden
          />
          {/* Obsidian tint layer over the video */}
          <div aria-hidden className="pointer-events-none absolute inset-0"
                style={{ background: "linear-gradient(180deg, rgba(5,5,6,0.55), rgba(5,5,6,0.7))" }} />

          {/* Bioluminescent cursor halo */}
          {cursor.visible && (
            <div
              aria-hidden
              className="pointer-events-none absolute rounded-full mix-blend-screen"
              style={{
                left: cursor.x - 140, top: cursor.y - 140,
                width: 280, height: 280,
                background: "radial-gradient(circle, rgba(47,185,153,0.35) 0%, rgba(47,185,153,0.08) 45%, transparent 70%)",
                filter: "blur(6px)",
                transition: "opacity 0.25s ease",
              }}
            />
          )}

          {/* SVG grid overlay */}
          <svg aria-hidden preserveAspectRatio="none"
                className="absolute inset-0 w-full h-full opacity-[0.08]"
                viewBox="0 0 1200 800">
            <defs>
              <pattern id="eliteNavGrid" width="56" height="56" patternUnits="userSpaceOnUse">
                <path d="M 56 0 L 0 0 0 56" fill="none"
                      stroke="rgba(216,178,62,0.5)" strokeWidth="0.4" />
              </pattern>
            </defs>
            <rect width="1200" height="800" fill="url(#eliteNavGrid)" />
          </svg>

          {/* Top strip · brand + close */}
          <div className="relative flex items-center justify-between px-8 md:px-14 pt-6">
            <div className="flex items-center gap-3">
              <img src="/logo-mark.png" alt="Vinci mark" className="h-7 w-auto" />
              <span className="hidden sm:flex flex-col leading-none">
                <span className="font-serif text-lg tracking-tight text-[#F5F5F0]">Vinci Inc.</span>
                <span className="font-grotesk text-[8px] uppercase tracking-[0.32em] text-[#8A6A1A] mt-1">
                  Elite · Executive Terminal
                </span>
              </span>
            </div>
            <button
              data-testid="elite-nav-close"
              onClick={onClose}
              className="group inline-flex items-center gap-2 border border-[#D8B23E]/40 hover:border-[#D8B23E] px-4 py-2 rounded-sm font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#D8B23E] hover:bg-[#D8B23E]/10 transition-colors"
              aria-label="Close menu"
            >
              <X size={13} /> Close
            </button>
          </div>

          {/* 3-column body */}
          <div className="relative grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.55fr)_minmax(0,1fr)] gap-8 px-8 md:px-14 pt-10 pb-24 h-[calc(100%-6rem)] overflow-y-auto">
            {/* LEFT · Vinci Shield */}
            <motion.div
              initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="hidden lg:flex flex-col items-start justify-center gap-6 pr-4"
            >
              <VinciShield />
              <div>
                <div className="font-grotesk text-[9px] uppercase tracking-[0.4em] text-[#8A6A1A]">
                  The Vinci Motto
                </div>
                <div className="mt-2 text-[#F5F5F0]"
                      style={{ fontFamily: "'Cinzel', 'Playfair Display', serif",
                                fontWeight: 700, fontSize: "clamp(1.4rem, 2.2vw, 2rem)",
                                letterSpacing: "0.12em" }}>
                  <span>Veni.</span>{" "}
                  <span className="italic bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] bg-clip-text text-transparent">Vidi.</span>{" "}
                  <span>Vici.</span>
                </div>
                <div className="mt-3 font-body text-[12px] text-[#C9C9C2] italic leading-relaxed max-w-xs">
                  I came. I saw. I conquered. Vinci moves through the
                  Indian construction landscape at that same tempo.
                </div>
              </div>
              <Link
                to={user ? accountPath : "/login"}
                onClick={onClose}
                data-testid="elite-nav-account"
                className="inline-flex items-center gap-2 border border-[#D8B23E]/50 px-4 py-2 rounded-sm text-[#D8B23E] hover:bg-[#D8B23E] hover:text-black font-grotesk text-[10px] uppercase tracking-[0.32em] transition-colors"
              >
                {user
                  ? (user.role === "admin" ? "Trade Desk" : "My Account")
                  : "Sign in"}
                <ArrowUpRight size={12} />
              </Link>
            </motion.div>

            {/* CENTRE · primary links */}
            <div className="flex flex-col justify-center gap-2 lg:gap-3">
              <div className="mb-3 font-grotesk text-[9px] uppercase tracking-[0.4em] text-[#8A6A1A]">
                Primary Navigation · {CORE_LINKS.length} destinations
              </div>
              {CORE_LINKS.map((l, i) => (
                <EliteLink
                  key={l.to}
                  index={i}
                  link={l}
                  isHovered={hovered === l.to}
                  onEnter={() => setHovered(l.to)}
                  onLeave={() => setHovered(null)}
                  onGo={() => go(l.to)}
                />
              ))}
              <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap gap-x-5 gap-y-2" data-testid="elite-nav-sections">
                <span className="font-grotesk text-[9px] uppercase tracking-[0.4em] text-[#8A6A1A] w-full mb-1">Homepage sections</span>
                {SECTION_LINKS.map((sec) => (
                  <button key={sec.href} onClick={() => goSection(sec.href)} data-testid={`elite-nav-section-${sec.label.toLowerCase()}`}
                          className="font-grotesk text-[10px] uppercase tracking-[0.26em] text-[#C9C9C2] hover:text-[#D8B23E] transition-colors">{sec.label}</button>
                ))}
              </div>
            </div>

            {/* RIGHT · Live Market Pulse */}
            <motion.aside
              initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="flex flex-col gap-4"
              data-testid="elite-nav-market-pulse"
            >
              <div>
                <div className="font-grotesk text-[9px] uppercase tracking-[0.4em] text-[#2FB999] inline-flex items-center gap-2">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-[#2FB999] opacity-70 animate-ping" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#2FB999]" />
                  </span>
                  Live Market Pulse
                </div>
                <div className="mt-2 text-[#F5F5F0]"
                      style={{ fontFamily: "'Cinzel', serif", fontWeight: 600,
                                fontSize: "clamp(1.05rem, 1.4vw, 1.3rem)",
                                letterSpacing: "0.08em" }}>
                  Rates · Trading Now
                </div>
                <p className="mt-1 font-body text-[11px] text-[#8A8A82]">
                  Streaming from Vinci Rate Ledger · India-wide averages.
                </p>
              </div>

              {rates.length ? (
                <>
                  {/* Top 6 prominent tiles */}
                  <div className="grid grid-cols-2 gap-2">
                    {rates.slice(0, 6).map((r, i) => (
                      <PulseTile key={`${r.label}-${i}`} rate={r} />
                    ))}
                  </div>
                  {/* Marquee for the remaining rates */}
                  {rates.length > 6 && (
                    <div className="border-t border-white/[0.07] pt-3">
                      <Marquee speed={30} gradient={false} pauseOnHover autoFill>
                        {rates.slice(6).map((r, i) => (
                          <MiniChip key={`${r.label}-mini-${i}`} rate={r} />
                        ))}
                      </Marquee>
                    </div>
                  )}
                </>
              ) : (
                <div className="border border-dashed border-white/10 rounded-sm p-6 text-center">
                  <div className="font-grotesk text-[9px] uppercase tracking-[0.3em] text-[#8A8A82]">
                    Fetching live rates…
                  </div>
                </div>
              )}

              <Link
                to="/trends"
                onClick={onClose}
                data-testid="elite-nav-trends-link"
                className="mt-2 inline-flex items-center justify-center gap-2 border border-[#2FB999]/40 px-4 py-2 rounded-sm text-[#2FB999] hover:bg-[#2FB999] hover:text-black font-grotesk text-[10px] uppercase tracking-[0.32em] transition-colors"
              >
                Enter Full Price Pulse Board <ArrowUpRight size={12} />
              </Link>
            </motion.aside>
          </div>

          {/* Bottom kicker */}
          <div className="absolute bottom-0 left-0 right-0 border-t border-white/[0.06] bg-black/40 backdrop-blur-md px-8 md:px-14 py-3 flex items-center justify-between text-[9px] font-grotesk uppercase tracking-[0.32em] text-[#6f6f68]">
            <span>Vinci · Elite Navigation · Press <b className="text-[#D8B23E]">ESC</b> to close</span>
            <span>Est. 2026 · Trade Asia</span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}


/* ─── Monumental hamburger trigger ────────────────────────────── */
export function MonumentalMenuTrigger({ onOpen, className = "" }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="elite-menu-trigger"
      aria-label="Open Vinci Elite Navigation"
      className={`group inline-flex items-center gap-3 pl-1 pr-3 py-1.5 rounded-sm hover:bg-white/[0.04] transition-colors ${className}`}
    >
      <span className="flex flex-col gap-[6px] py-0.5">
        <span className="block w-7 h-[1.5px] bg-[#F5F5F0] group-hover:bg-[#D8B23E] transition-colors" />
        <span className="block w-5 h-[1.5px] bg-[#D8B23E]" />
        <span className="block w-7 h-[1.5px] bg-[#F5F5F0] group-hover:bg-[#D8B23E] transition-colors" />
      </span>
      <span
        className="hidden sm:inline text-[#D8B23E] group-hover:text-[#F6E4A6] transition-colors"
        style={{ fontFamily: "'Cinzel', 'Playfair Display', serif",
                  fontWeight: 700, letterSpacing: "0.28em",
                  fontSize: "0.72rem" }}
      >
        MENU
      </span>
    </button>
  );
}


/* ─── Elite link row ──────────────────────────────────────────── */
function EliteLink({ index, link, isHovered, onEnter, onLeave, onGo }) {
  return (
    <motion.button
      type="button"
      onClick={onGo}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      data-testid={`elite-nav-${link.mono.toLowerCase()}`}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.18 + index * 0.05, duration: 0.55,
                     ease: [0.16, 1, 0.3, 1] }}
      className="group relative w-full text-left flex items-center gap-5 py-3 pr-4 pl-3 border-l border-transparent hover:border-[#D8B23E] transition-colors"
    >
      {/* Monogram pill */}
      <span
        className="shrink-0 grid place-items-center w-11 h-11 rounded-sm border border-[#D8B23E]/40 text-[#D8B23E] font-mono text-[11px] tracking-widest"
        style={{
          background: "linear-gradient(135deg, rgba(216,178,62,0.10), rgba(0,0,0,0.35))",
          boxShadow: isHovered
            ? "0 0 30px rgba(216,178,62,0.5), inset 0 0 12px rgba(216,178,62,0.25)"
            : "inset 0 0 8px rgba(216,178,62,0.12)",
          animation: "vinciMonoPulse 3.6s ease-in-out infinite",
        }}
      >
        {link.gold ? (
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" data-testid="elite-nav-pd-diamond"><path d="M6 3h12l4 6-10 12L2 9l4-6z" /><path d="M2 9h20M9 3l3 18M15 3l-3 18" /></svg>
        ) : link.mono}
      </span>
      {/* Label + caption */}
      <span className="flex-1 min-w-0">
        <span
          className="block text-[#F5F5F0] group-hover:bg-gradient-to-r group-hover:from-[#D8B23E] group-hover:via-[#F6E4A6] group-hover:to-[#B87333] group-hover:bg-clip-text group-hover:text-transparent transition-colors"
          style={{
            fontFamily: "'Cinzel', 'Playfair Display', serif",
            fontWeight: 600,
            letterSpacing: "0.06em",
            fontSize: "clamp(1.35rem, 2.4vw, 2.15rem)",
            lineHeight: 1.05,
          }}
        >
          {link.label}
        </span>
        <AnimatePresence>
          {isHovered && (
            <motion.span
              key="cap"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="block font-body text-[11px] italic text-[#2FB999] mt-1 pl-0.5"
              dangerouslySetInnerHTML={{ __html: link.caption }}
            />
          )}
        </AnimatePresence>
      </span>
      {/* Right chevron */}
      <ArrowUpRight
        size={16}
        className={`shrink-0 transition-all ${isHovered ? "text-[#D8B23E] translate-x-0.5 -translate-y-0.5" : "text-[#8A8A82]"}`}
      />
      <style>{`
        @keyframes vinciMonoPulse {
          0%, 100% { text-shadow: 0 0 4px rgba(216,178,62,0.5); }
          50%      { text-shadow: 0 0 12px rgba(246,228,166,0.85); }
        }
      `}</style>
    </motion.button>
  );
}


/* ─── Live rate widgets ───────────────────────────────────────── */
function PulseTile({ rate }) {
  const up = rate.trend === "up";
  const down = rate.trend === "down";
  const color = up ? "#2FB999" : down ? "#C0654E" : "#8A8A82";
  const TrendIcon = up ? TrendingUp : down ? TrendingDown : Minus;
  return (
    <div
      className="border border-white/[0.06] rounded-sm px-3 py-2.5 hover:border-[#D8B23E]/40 transition-colors"
      style={{
        background: "linear-gradient(135deg, rgba(216,178,62,0.04), rgba(0,0,0,0.25))",
        backdropFilter: "blur(8px)",
      }}
    >
      <div className="font-grotesk text-[8.5px] uppercase tracking-[0.22em] text-[#8A6A1A] truncate">
        {(rate.brand ? `${rate.brand} · ` : "") + (rate.label || rate.category)}
      </div>
      <div className="mt-1 flex items-baseline justify-between gap-2">
        <span className="font-mono text-[15px] text-[#F5F5F0]">
          ₹{Number(rate.price || 0).toLocaleString("en-IN")}
        </span>
        <span className="font-mono text-[10px] inline-flex items-center gap-1" style={{ color }}>
          <TrendIcon size={10} />
          {rate.change_pct != null ? `${Math.abs(rate.change_pct)}%` : "—"}
        </span>
      </div>
    </div>
  );
}
function MiniChip({ rate }) {
  const up = rate.trend === "up";
  const down = rate.trend === "down";
  const color = up ? "#2FB999" : down ? "#C0654E" : "#8A8A82";
  return (
    <span className="inline-flex items-center gap-2 mx-4 whitespace-nowrap font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#8A8A82]">
      {(rate.brand ? `${rate.brand} · ` : "") + (rate.label || rate.category)}
      <span className="font-mono text-[10px] text-[#F5F5F0]">
        ₹{Number(rate.price || 0).toLocaleString("en-IN")}
      </span>
      <span className="font-mono text-[10px]" style={{ color }}>
        {rate.change_pct != null ? `${up ? "▲" : down ? "▼" : "•"} ${Math.abs(rate.change_pct)}%` : "—"}
      </span>
    </span>
  );
}


/* ─── Vinci Shield ────────────────────────────────────────────── */
function VinciShield() {
  return (
    <div className="relative" aria-hidden>
      <div
        className="w-40 h-40 grid place-items-center relative"
        style={{
          filter: "drop-shadow(0 0 40px rgba(216,178,62,0.35))",
        }}
      >
        <svg viewBox="0 0 200 200" className="w-full h-full"
              style={{ animation: "vinciShieldFloat 6s ease-in-out infinite" }}>
          <defs>
            <linearGradient id="shieldGold" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%"   stopColor="#7A5410" />
              <stop offset="50%"  stopColor="#D8B23E" />
              <stop offset="100%" stopColor="#F6E4A6" />
            </linearGradient>
            <filter id="shieldGlow">
              <feGaussianBlur stdDeviation="2.4" result="b" />
              <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          </defs>
          {/* Shield outline */}
          <path
            d="M100 12 L182 46 L182 108 C182 148 148 178 100 190 C52 178 18 148 18 108 L18 46 Z"
            fill="none" stroke="url(#shieldGold)" strokeWidth="1.6"
            filter="url(#shieldGlow)"
          />
          <path
            d="M100 26 L168 54 L168 108 C168 140 140 166 100 176 C60 166 32 140 32 108 L32 54 Z"
            fill="rgba(216,178,62,0.05)" stroke="rgba(216,178,62,0.35)" strokeWidth="0.6"
          />
          {/* Central VI monogram */}
          <text x="100" y="118" textAnchor="middle" fill="url(#shieldGold)"
                style={{ fontFamily: "'Cinzel', serif", fontWeight: 900,
                          fontSize: 56, letterSpacing: "0.05em" }}
                filter="url(#shieldGlow)">
            VI
          </text>
          {/* Tick marks */}
          {[...Array(12)].map((_, i) => {
            const a = (i / 12) * Math.PI * 2;
            const x1 = 100 + Math.cos(a) * 78;
            const y1 = 100 + Math.sin(a) * 78;
            const x2 = 100 + Math.cos(a) * 85;
            const y2 = 100 + Math.sin(a) * 85;
            return (
              <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
                    stroke="rgba(216,178,62,0.5)" strokeWidth="0.6" />
            );
          })}
        </svg>
      </div>
      <style>{`
        @keyframes vinciShieldFloat {
          0%, 100% { transform: translateY(0) rotate(0); }
          50%      { transform: translateY(-6px) rotate(-0.4deg); }
        }
      `}</style>
    </div>
  );
}
